import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Speaks assistant replies aloud with window.speechSynthesis.
 *
 * Two ways in:
 *   speak(text, { onEnd })            — a finished reply, spoken in one go.
 *   begin() / push(chunk) / end({onEnd}) — a reply still streaming in. Complete
 *     sentences are handed to the synthesizer as they arrive, so the voice
 *     starts on sentence one instead of waiting for the whole SSE stream. This
 *     is most of the perceived latency in hands-free modes.
 *
 * Half duplex is a hard constraint of the surrounding design: the mic hears
 * the speaker (measured on Android Chrome), so callers close the mic before
 * speaking and reopen it from onEnd — never overlap them, unless barge-in is
 * explicitly enabled and the caller accepts the echo risk.
 */

// Chrome silently truncates long utterances (roughly 15s in), so text is
// spoken as a queue of shorter chunks split on sentence boundaries.
const MAX_CHUNK_CHARS = 180;

const RATE_KEY = 'ctr.speech.rate';
const VOICE_KEY = 'ctr.speech.voiceURI';

export const RATE_OPTIONS = [0.85, 1, 1.15, 1.3];

function loadRate() {
  try {
    const n = parseFloat(localStorage.getItem(RATE_KEY));
    return Number.isFinite(n) && n >= 0.5 && n <= 2 ? n : 1;
  } catch {
    return 1;
  }
}

function loadVoiceURI() {
  try {
    return localStorage.getItem(VOICE_KEY) || '';
  } catch {
    return '';
  }
}

/** Strip markdown so the voice doesn't read "asterisk asterisk" out loud. */
export function forSpeech(text) {
  return (text || '')
    .replace(/```[\s\S]*?```/g, ' code block ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s*#{1,6}\s*/gm, '')
    .replace(/(\*\*|__|\*|_)/g, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    // Emoji read literally ("knife and fork") — the bubble keeps them, the
    // spoken version doesn't.
    .replace(
      /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}\u{FE00}-\u{FE0F}\u{200D}]/gu,
      ''
    )
    .replace(/\s+/g, ' ')
    .trim();
}

/** Split into speakable chunks, never mid-sentence unless one is very long. */
function chunk(text) {
  const sentences = text.match(/[^.!?]+[.!?]*\s*/g) || [text];
  const out = [];
  let buf = '';
  for (const s of sentences) {
    if ((buf + s).length > MAX_CHUNK_CHARS && buf) {
      out.push(buf.trim());
      buf = s;
    } else {
      buf += s;
    }
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

/** Leading complete sentences of `buf`, and whatever tail is still partial. */
function takeComplete(buf) {
  // A sentence ends at .!? followed by whitespace. A very long run with no
  // terminator is released anyway so a rambling reply still starts speaking.
  const match = buf.match(/^[\s\S]*[.!?]["')\]]?\s/);
  if (match) {
    return { ready: match[0], rest: buf.slice(match[0].length) };
  }
  if (buf.length > MAX_CHUNK_CHARS * 2) {
    const cut = buf.lastIndexOf(' ', MAX_CHUNK_CHARS);
    if (cut > 0) return { ready: buf.slice(0, cut), rest: buf.slice(cut) };
  }
  return { ready: '', rest: buf };
}

export default function useSpeechOutput() {
  const [speaking, setSpeaking] = useState(false);
  const [voices, setVoices] = useState([]);
  const [rate, setRateState] = useState(loadRate);
  const [voiceURI, setVoiceURIState] = useState(loadVoiceURI);

  const supported = typeof window !== 'undefined' && Boolean(window.speechSynthesis);

  // Guards against a cancelled run's queued utterances firing onEnd late and
  // reopening the mic underneath a newer turn.
  const runRef = useRef(0);
  const queuedRef = useRef(0);
  const finishedRef = useRef(0);
  const endedRef = useRef(false);
  const bufferRef = useRef('');
  const onEndRef = useRef(null);
  const unlockedRef = useRef(false);
  const watchdogRef = useRef(null);
  // Live prefs for the utterance factory (state would be stale in callbacks).
  const rateRef = useRef(rate);
  const voiceURIRef = useRef(voiceURI);

  useEffect(() => {
    rateRef.current = rate;
  }, [rate]);
  useEffect(() => {
    voiceURIRef.current = voiceURI;
  }, [voiceURI]);

  // Chrome populates the voice list asynchronously and reports an empty array
  // until it does; Safari fills it on first access.
  useEffect(() => {
    if (!supported) return undefined;
    const synth = window.speechSynthesis;
    const read = () => {
      const list = synth.getVoices?.() || [];
      if (list.length) setVoices(list);
    };
    read();
    synth.addEventListener?.('voiceschanged', read);
    return () => synth.removeEventListener?.('voiceschanged', read);
  }, [supported]);

  const setRate = useCallback((next) => {
    setRateState(next);
    try {
      localStorage.setItem(RATE_KEY, String(next));
    } catch {
      /* private mode */
    }
  }, []);

  const setVoiceURI = useCallback((next) => {
    setVoiceURIState(next || '');
    try {
      if (next) localStorage.setItem(VOICE_KEY, next);
      else localStorage.removeItem(VOICE_KEY);
    } catch {
      /* private mode */
    }
  }, []);

  const pickVoice = useCallback(() => {
    const list = window.speechSynthesis.getVoices?.() || [];
    if (!list.length) return null;
    const chosen = voiceURIRef.current
      ? list.find((v) => v.voiceURI === voiceURIRef.current)
      : null;
    if (chosen) return chosen;
    // No preference (or it vanished with a system update): a local en-US voice
    // beats a remote one — remote voices stall when the network hiccups.
    return (
      list.find((v) => v.localService && /^en[-_]US/i.test(v.lang)) ||
      list.find((v) => /^en[-_]US/i.test(v.lang)) ||
      list.find((v) => /^en/i.test(v.lang)) ||
      list[0]
    );
  }, []);

  const stopWatchdog = useCallback(() => {
    if (watchdogRef.current) {
      window.clearInterval(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  /** Fire onEnd exactly once, when the last queued chunk has been spoken. */
  const settle = useCallback(
    (run) => {
      if (run !== runRef.current) return; // superseded by a newer run
      if (!endedRef.current || finishedRef.current < queuedRef.current) return;
      stopWatchdog();
      setSpeaking(false);
      const cb = onEndRef.current;
      onEndRef.current = null;
      cb?.();
    },
    [stopWatchdog]
  );

  /**
   * Desktop Chrome pauses synthesis on its own (the ~15s bug) and sometimes
   * drops the final `onend` entirely, which would strand the caller waiting
   * for a turn that already finished. Poll: nudge a paused engine, and if the
   * engine is idle while we still expect callbacks, settle anyway.
   */
  const startWatchdog = useCallback(
    (run) => {
      stopWatchdog();
      watchdogRef.current = window.setInterval(() => {
        if (run !== runRef.current) {
          stopWatchdog();
          return;
        }
        const synth = window.speechSynthesis;
        if (!synth) return;
        try {
          if (synth.paused) synth.resume();
        } catch {
          /* nothing queued */
        }
        if (!synth.speaking && !synth.pending && endedRef.current) {
          finishedRef.current = queuedRef.current;
          settle(run);
        }
      }, 500);
    },
    [settle, stopWatchdog]
  );

  const enqueue = useCallback(
    (text, run) => {
      const parts = chunk(text);
      parts.forEach((part) => {
        if (!part) return;
        queuedRef.current += 1;
        const u = new SpeechSynthesisUtterance(part);
        const voice = pickVoice();
        if (voice) {
          u.voice = voice;
          u.lang = voice.lang;
        } else {
          u.lang = 'en-US';
        }
        u.rate = rateRef.current;
        const done = () => {
          if (run !== runRef.current) return;
          finishedRef.current += 1;
          settle(run);
        };
        u.onend = done;
        u.onerror = done;
        try {
          window.speechSynthesis.speak(u);
        } catch {
          done();
        }
      });
    },
    [pickVoice, settle]
  );

  const cancel = useCallback(() => {
    runRef.current += 1;
    onEndRef.current = null;
    endedRef.current = false;
    bufferRef.current = '';
    queuedRef.current = 0;
    finishedRef.current = 0;
    stopWatchdog();
    const synth = window.speechSynthesis;
    if (synth) {
      try {
        // Chrome can leave synthesis paused (its auto-pause), a state where
        // cancel() is silently ignored and the queued chunks keep playing.
        // Resume first so the cancel lands, then clear again on the next tick
        // for utterances Chrome leaves queued after the first.
        synth.resume();
        synth.cancel();
        setTimeout(() => {
          try {
            synth.cancel();
          } catch {
            /* already clear */
          }
        }, 0);
      } catch {
        /* nothing queued */
      }
    }
    setSpeaking(false);
  }, [stopWatchdog]);

  /** Open a streaming utterance. Returns the run id the caller can ignore. */
  const begin = useCallback(() => {
    if (!supported) return 0;
    runRef.current += 1;
    const run = runRef.current;
    queuedRef.current = 0;
    finishedRef.current = 0;
    endedRef.current = false;
    bufferRef.current = '';
    onEndRef.current = null;
    try {
      window.speechSynthesis.cancel();
    } catch {
      /* nothing queued */
    }
    setSpeaking(true);
    startWatchdog(run);
    return run;
  }, [supported, startWatchdog]);

  /** Feed streamed reply text; complete sentences start speaking immediately. */
  const push = useCallback(
    (text) => {
      if (!supported || !text) return;
      const run = runRef.current;
      bufferRef.current += text;
      for (;;) {
        const { ready, rest } = takeComplete(bufferRef.current);
        if (!ready) break;
        bufferRef.current = rest;
        const clean = forSpeech(ready);
        if (clean) enqueue(clean, run);
      }
    },
    [supported, enqueue]
  );

  /** No more text is coming. onEnd fires once the queue has drained. */
  const end = useCallback(
    ({ onEnd } = {}) => {
      if (!supported) {
        // The caller still needs its turn-ended signal, or the loop stalls.
        onEnd?.();
        return;
      }
      const run = runRef.current;
      onEndRef.current = onEnd || null;
      const tail = forSpeech(bufferRef.current);
      bufferRef.current = '';
      if (tail) enqueue(tail, run);
      endedRef.current = true;
      if (queuedRef.current === 0) {
        // Nothing speakable at all (an empty or emoji-only reply).
        stopWatchdog();
        setSpeaking(false);
        onEndRef.current = null;
        onEnd?.();
        return;
      }
      settle(run);
    },
    [supported, enqueue, settle, stopWatchdog]
  );

  /** One-shot: speak a finished string. */
  const speak = useCallback(
    (text, { onEnd } = {}) => {
      const clean = forSpeech(text);
      if (!supported || !clean) {
        onEnd?.();
        return;
      }
      begin();
      push(clean);
      end({ onEnd });
    },
    [supported, begin, push, end]
  );

  /**
   * Mobile browsers block speechSynthesis.speak() unless it has been invoked
   * at least once from a real user gesture. Our only gesture is a tap that
   * uses getUserMedia (not speech), so the later reply would be silently
   * dropped. Priming a silent utterance inside the gesture unlocks the rest
   * of the session. Call from an onClick, never from an effect.
   */
  const unlock = useCallback(() => {
    if (unlockedRef.current || !supported) return;
    unlockedRef.current = true;
    try {
      window.speechSynthesis.getVoices?.(); // nudges the voice list to load
      const u = new SpeechSynthesisUtterance(' ');
      u.volume = 0;
      window.speechSynthesis.speak(u);
    } catch {
      /* best effort */
    }
  }, [supported]);

  useEffect(
    () => () => {
      runRef.current += 1;
      onEndRef.current = null;
      if (watchdogRef.current) window.clearInterval(watchdogRef.current);
      try {
        window.speechSynthesis?.cancel();
      } catch {
        /* nothing queued */
      }
    },
    []
  );

  return {
    speak,
    begin,
    push,
    end,
    cancel,
    unlock,
    speaking,
    supported,
    voices,
    rate,
    setRate,
    voiceURI,
    setVoiceURI,
  };
}
